import { UserService } from '@fnv-core/authentication';
import { Api } from '@fnv-core/http';
import { FormControlProps } from '../../interfaces/FormControlProps';
import { StringHelpers } from '../../helpers/StringHelpers';
import { DatasourceOptionsService } from '../../services/DatasourceOptionsService';
import { DatasourceLoaderStateService } from '../../services/DatasourceLoaderStateService';
import { FormControlHelpers } from './FormControlHelpers';
import { FormFieldStateHelpers } from './FormFieldStateHelpers';
import { IFormFieldDefinition } from '../../interfaces/Fields';

export interface IOption {
    value: string;
    label: string;
    text?: string;
    parent? : string;
    data: any;
    icon? : string;
}

export class ComponentStoreHelpers {
    static convertUnicode(input: string) {
        if (input !== undefined) {
            var index: number = input.indexOf('A\u008f');
            if (index >= 0) {
                return input.replace('A\u008f', 'Å');
            }
            return input;
        }
        return '';
    }

    /**
     * Maps the options array for the component to a globally used IOption model.
     * 
     * @returns A mapping function to map the item to the option model.
     */
    static restDataMapper(component: React.Component<FormControlProps>): (item: any, index: number) => IOption {
        const formFieldDefinition = component.props.formFieldDefinition;
        return (option, index): IOption => {
            let optionLabel = '';
            if (formFieldDefinition.resultMode === 'array') {
                let data: any = {};
                data[formFieldDefinition.propertyNameInArrayMode] = option;
                return {
                    value: option,
                    label: option,
                    text: option,
                    parent: '',
                    data: data 
                };
            }
            if (option[formFieldDefinition.optionField]) {
                optionLabel = this.convertUnicode(option[formFieldDefinition.optionField]);
            } else {
                optionLabel = this.convertUnicode(StringHelpers.replacePropertiesInString(formFieldDefinition.optionField, 
                    {
                        ...component.props.formController.getPayload(),
                        ...component.props.formikBag.values,
                        ...option
                    }, true));
            }            
            return {
                value: option[formFieldDefinition.valueField],
                label: optionLabel,
                text: optionLabel,
                parent: option.Link ? option.Link : '',
                data: option
            };
        };
    }

    static kenticoOptionsMapper(component: React.Component<FormControlProps>) {
        const formFieldDefinition = component.props.formFieldDefinition;
        return (option, index): IOption => ({
            value: formFieldDefinition.valueoptions[index],
            label: option,
            text: option,
            data: {},
            icon: (formFieldDefinition.iconoptions && formFieldDefinition.iconoptions[index]) || ""
        });
    }

    static hasDatasource(component: React.Component<FormControlProps>): boolean {
        return ComponentStoreHelpers.hasFieldDatasource(component.props.formFieldDefinition);
    }

    static hasFieldDatasource(formFieldDefinition: IFormFieldDefinition): boolean {
        return formFieldDefinition.datasource;
    }

    static getValueData(component: React.Component<FormControlProps>, value: any) : string {

        // Conveniance
        const formFieldDefinition = component.props.formFieldDefinition;
        
        if (!component.state[formFieldDefinition.datasource]) {
            return '';
        }

        // Checks if the given item is part of the values
        // Also takes into accounts multiple values that are joined by a comma
        const checkContainsValue = (item) => {
          const itemValue = item[formFieldDefinition.valueField];
          if (itemValue == null) {
            return false;
          }

          if (typeof (value) !== 'string') {
            return itemValue == value;
          }
          
          const values = value.split(',');
          if (!values.length) {
            return false;
          }

          if (values.length > 1) {
            return values.indexOf(itemValue) > -1;
          }

          return values[0] == itemValue;
        };

        let result = component.state[formFieldDefinition.datasource].filter((item) => {
            return checkContainsValue(item);
        }).map((item) => item[formFieldDefinition.optionField]);

        if (result.length === 1) {
          return result[0];
        }

        if (result.length > 1) {
            return result;
        }

        return '';
    }

    static getValueDataForPayload(component: React.Component<FormControlProps>, value: any) : any {

        // Conveniance
        const formFieldDefinition = component.props.formFieldDefinition;
        
        if (!component.state[formFieldDefinition.datasource]) {
            return "";
        }

        let result = component.state[formFieldDefinition.datasource].filter((item) => {
            return item[formFieldDefinition.valueField] == value;            
        }).map((item) => item);

        if (result.length > 0) {
            return result[0];
        }
        return {};
    }

    static getListData(
        component: React.Component<FormControlProps>,
        restDataMapper?,
        kenticoOptionsMapper?
    ): IOption[] {
        // If no values given, we fall back to the defaults.
        // Due to the dependency to component, we instantiate the defaults like this:
        restDataMapper = restDataMapper || this.restDataMapper(component);
        kenticoOptionsMapper = kenticoOptionsMapper || this.kenticoOptionsMapper(component);

        // Conveniance
        const formFieldDefinition = component.props.formFieldDefinition;

        // Initial value
        let dataset = [];

        // Preferably we use the data from an external datasource
        // We use the given mapper
        if (component.state && component.state[formFieldDefinition.datasource]
		&& component.state[formFieldDefinition.datasource].map !== undefined) {
            dataset = component.state[formFieldDefinition.datasource].map(restDataMapper);
        } // If not present, we like to use options
        else if (formFieldDefinition.options) {
            dataset = formFieldDefinition.options.map(kenticoOptionsMapper);
        }
        // Otherwise, the array is initial / empty

        // execute mapping if method is given
        return dataset;
    }

    static setFormFieldData(component: React.Component<FormControlProps>, data) {
        component.setState({ [component.props.formFieldDefinition.datasource]: data });
    }

    static getFormFieldData(component: React.Component<FormControlProps>) {
        return component.state[component.props.formFieldDefinition.datasource] || {};
    }

    static addMontsToDate(date: Date, addMonths? : number) {        
        let newDate = new Date(date.setMonth(date.getMonth() + addMonths));
        let month = '' + (newDate.getMonth() + 1);
        let day = '' + newDate.getDate();
        let year = newDate.getFullYear();
    
        if (month.length < 2) {
            month = '0' + month;
        }

        if (day.length < 2) {
            day = '0' + day;
        }
    
        return [year, month, day].join('-');
    }



    static getCurrentDate(addMonths? : number) {
        return this.addMontsToDate(new Date() , addMonths || 0);
    }

    static getDataUrl(props: FormControlProps, parameters: string = ''): string {
        return props.formConfig.urlMappings[props.formFieldDefinition.datasource] + parameters;
    }

    static clearFields = (component: React.Component<FormControlProps>) => {
        FormFieldStateHelpers.clearMarkedFields(component);
        const { formFieldDefinition, formController } = {...component.props};
        if (!formFieldDefinition.isDependentField) {
            return;
        }
        let clearFields : any = {};
        if (!!formFieldDefinition.clearFormStateForFieldsPrefetch && formFieldDefinition.clearFields) {
            formFieldDefinition.clearFields.map((field) => {
                clearFields[field] = "";
            });
            if (!!formFieldDefinition.clearPayloadForFieldsPrefetch) {
                formController.setPayload(clearFields);
            }
        }                 
        formController.setState({[formFieldDefinition.name]: "", ...clearFields});
    }
    
    static getAsyncData(inputValue: string, component: React.Component<FormControlProps>) {
        
        return new Promise((resolve, reject) => {
          component.setState({ isLoading: true });              
            const getData = () => {
                if (inputValue.length > 1) {
                    ComponentStoreHelpers.getData(component, inputValue).then((result) => {
                        resolve(ComponentStoreHelpers.getListData(component));   
                        component.setState({ isLoading: false });              
                    }).catch(() => {
                        component.setState({ isLoading: false });              
                        reject();
                    });
                } else {
                    resolve(ComponentStoreHelpers.getListData(component));  
                    component.setState({ isLoading: false });              
                }
            };

            FormFieldStateHelpers.clearMarkedFields(component);
            if (component.props.formFieldDefinition.isDependentField) {  
                let clearFields : any = {};
                if (!!component.props.formFieldDefinition.clearFormStateForFieldsPrefetch && component.props.formFieldDefinition.clearFields) {
                    component.props.formFieldDefinition.clearFields.map((field) => {
                        clearFields[field] = "";
                    });
                    if (!!component.props.formFieldDefinition.clearPayloadForFieldsPrefetch) {
                        component.props.formController.setPayload(clearFields);
                    }
                }                 
                component.props.formController.setState({[component.props.formFieldDefinition.name]: "", ...clearFields}, () => {
                    getData();
                });
                return;
            }

            getData();
        });
    }

    static getData(component: React.Component<FormControlProps>, inputValue? : string): Promise<any> {
        DatasourceOptionsService.setOptionsForForm(component.props.formFieldDefinition.name, []);

        return new Promise(async (resolve, reject) => {
            let returnData = undefined;
            const datasource = component.props.formConfig.urlMappings[component.props.formFieldDefinition.datasource];
            if (!datasource) {
                resolve(returnData);
                return;
            }
            if (datasource && datasource.indexOf("datasource:") === 0) {
                const datasourceUrl = datasource.substring(11);
                if (datasourceUrl == "userroles") {
                    if ((component as any).unmounted) {
                        resolve(returnData);
                        return;
                    }
                    DatasourceOptionsService.setOptionsForForm(component.props.formFieldDefinition.name, UserService.getUserRoles());
                    this.setFormFieldData(component, UserService.getUserRoles());
                    resolve(returnData);
                    return;
                }
            }

            let url = ComponentStoreHelpers.getDataUrl(component.props);

            // parse {} from url and replace with predefined variabels 
            let regularExpressionForMatchingCurlyBrackets = /{.+?}/g;
            const matches = (url || "").match(regularExpressionForMatchingCurlyBrackets);
            if (matches) {
                let data = {
                    ...component.props.formController.getPayload(),
                    ...component.props.formikBag.values,
                    inputvalue : inputValue || ""
                };

                matches.map((match) => {    
                    const matchValue = match.slice(1, -1);
                    let value = "";
                    let field = matchValue.toLowerCase();
                    if (field == "id" || field == "userid" || field == "persoonid") {
                        value = UserService.getUserId();
                    } else 
                    if (field == "bondsid") {
                        value = UserService.getBondsId();
                    } else 
                    if (field == "consent") {
                        value = component.props.formFieldDefinition.optionyes;
                    } else
                    if (matchValue.indexOf("isodate:") == 0) {
                        const fieldName = matchValue.slice(8);
                        if (data[fieldName]) {
                            value = StringHelpers.convertNLDateToIso(data[fieldName]);
                        }                        
                    } else
                    if (field == "[currentdate]") {
                        value = ComponentStoreHelpers.getCurrentDate();
                    } else 
                    if (field == "[futuredate:2:month]" ) {
                        // TODO : refactor to make it non-static
                        value = ComponentStoreHelpers.getCurrentDate(2);
                    }

                    if (data[matchValue]) {
                        value = data[matchValue];
                        if ((value as any).value) {
                            value = (value as any).value;
                        }
                    }

                    url = url.replace("{" + matchValue + "}", value);
                });
            }

            if (url === undefined || typeof url === "undefined") {
                resolve(returnData);
                return;
            }
            DatasourceLoaderStateService.increaseLoadingCounter();
        
            let counter = 0;            
            if ((component as any).state && (component as any).setState) {
                counter = FormControlHelpers.getCounter(component as any); // keep track of latest request
            }

            const triggerDatasourceRequest = async () => {
              try {
                const data = await Api.get<any[]>(url, { cache: true });
                
                returnData = data;
                // if component gets unmounted during retrieving of data, then
                // changing react state will trigger an exception in react-dev mode
                // to prevent this, we set unmounted to true in componentWillUnmount
                //
                // this situation can happen because fillViaFormApi and other api calls
                // will trigger rerendering and recreating from a higher level (formcontainer)
                // and this conflicts with the async process here.
                if ((component as any).unmounted) {
                  DatasourceLoaderStateService.resetLoadingCounter();                    

                  resolve(returnData);
                  return;
                }
                DatasourceLoaderStateService.decreaseLoadingCounter();

                if ((component as any).state && (component as any).setState &&
                  (component.state as any).dataSourceCounter) {
                  if (counter < (component.state as any).dataSourceCounter) {
                    resolve(false);
                    return;
                  }
                }

                if (data) {
                  DatasourceOptionsService.setOptionsForForm(component.props.formFieldDefinition.name, data);
                  this.setFormFieldData(component, data);
                }
                resolve(returnData);
              } catch {
                DatasourceLoaderStateService.resetLoadingCounter();                    
                if ((component as any).unmounted) {
                  resolve(returnData);
                  return;
                }

                // set empty data when api gives error (dependent dropdowns can trigger a 404 from api on initial mount/view of page)
                this.setFormFieldData(component, []);
              }
            };

            if (!component.props.formFieldDefinition.useAutoDelayWhenTriggeringDatasource) {
              return await triggerDatasourceRequest();
            } else {
              let timeDelay = 50;
              if ((component as any).state && (component as any).setState) {
                if ((component as any).state.timer !== undefined) {
                    DatasourceLoaderStateService.resetLoadingCounter();
                }
                timeDelay = FormControlHelpers.getTimeDelay(component);
              }

              let timer = setTimeout(async () => {
                return await triggerDatasourceRequest();
              }, timeDelay);
              
              if ((component as any).state && (component as any).setState) {
                component.setState({
                  timer: timer 
                });
              } 
            }            
        });
    }
}
